// ============================================================================
// CRAFTING.JS — Recipe registry & crafting helpers for Palm Island
// ============================================================================
import { getDiscoveryCount } from '../ui/discovery.js';
import { canAfford as checkAfford } from './resources.js';

// ---------------------------------------------------------------------------
// Tier unlock thresholds — discovery count needed to unlock each tier
// ---------------------------------------------------------------------------
export const TIER_THRESHOLDS = {
  1: 5,   // Tier 1: 5+ discoveries
  2: 12,  // Tier 2: 12+ discoveries
  3: 18,  // Tier 3: 18+ (future)
  4: 24,  // Tier 4: 24+ (future)
};

// ---------------------------------------------------------------------------
// Recipe registry
// ---------------------------------------------------------------------------
export const RECIPE_REGISTRY = {
  // --- Tier 1 recipes (unlocked at 5+ discoveries) ---
  campfire: {
    id: 'campfire',
    name: 'Campfire',
    icon: '🔥',
    tier: 1,
    discoveryRequirement: 5,
    recipeVersion: 1,
    cost: {
      coconut_shell: 3,
      driftwood: 2,
    },
    placementRules: {
      surface: 'sand',
      minDistFromWater: 3,
      minDistFromOther: 2,
    },
    description: 'A cozy campfire with warm, flickering light',
    onPlace: null, // Tier 1 callbacks defined in pi-tx9.2
  },
  wind_chime: {
    id: 'wind_chime',
    name: 'Shell Wind Chime',
    icon: '🎐',
    tier: 1,
    discoveryRequirement: 5,
    recipeVersion: 1,
    cost: {
      seashell: 4,
      driftwood: 1,
    },
    placementRules: {
      surface: 'near_tree',  // must be within 3 units of a palm tree
      minDistFromWater: 0,
      minDistFromOther: 2,
    },
    description: 'Tinkling shells that catch the ocean breeze',
    onPlace: null,
  },
  tiki_torch: {
    id: 'tiki_torch',
    name: 'Tiki Torch',
    icon: '🔦',
    tier: 1,
    discoveryRequirement: 5,
    recipeVersion: 1,
    cost: {
      driftwood: 3,
      coconut_shell: 1,
    },
    placementRules: {
      surface: 'sand',
      minDistFromWater: 1,
      minDistFromOther: 2,
    },
    description: 'A tropical torch with a warm, dancing flame',
    onPlace: null,
  },

  // --- Tier 2 recipes (unlocked at 12+ discoveries) ---
  hammock: {
    id: 'hammock',
    name: 'Hammock',
    icon: '🛏️',
    tier: 2,
    discoveryRequirement: 12,
    recipeVersion: 1,
    cost: {
      driftwood: 4,
      seashell: 2,
      coconut_shell: 2,
    },
    placementRules: {
      surface: 'between_trees',  // must be between 2 palm trees < 8 units apart
      minDistFromWater: 2,
      minDistFromOther: 3,
    },
    description: 'A relaxing hammock strung between two palms',
    onPlace: null,
  },
  fishing_rod_stand: {
    id: 'fishing_rod_stand',
    name: 'Fishing Rod Stand',
    icon: '🎣',
    tier: 2,
    discoveryRequirement: 12,
    recipeVersion: 1,
    cost: {
      driftwood: 3,
      fish: 2,
      glass_shard: 1,
    },
    placementRules: {
      surface: 'water_edge',  // must be near water
      minDistFromWater: 0,
      minDistFromOther: 2,
    },
    description: 'A stand for fishing at the water\'s edge',
    onPlace: null,
  },
  sand_castle: {
    id: 'sand_castle',
    name: 'Sand Castle',
    icon: '🏰',
    tier: 2,
    discoveryRequirement: 12,
    recipeVersion: 1,
    cost: {
      sand_dollar: 2,
      seashell: 3,
      coconut_shell: 2,
    },
    placementRules: {
      surface: 'sand',
      minDistFromWater: 2,
      minDistFromOther: 2,
    },
    description: 'A majestic sand castle that erodes over time',
    onPlace: null,
  },
  flower_garden: {
    id: 'flower_garden',
    name: 'Flower Garden',
    icon: '🌺',
    tier: 2,
    discoveryRequirement: 12,
    recipeVersion: 1,
    cost: {
      coconut_shell: 3,
      melody_fragment: 2,
      sand_dollar: 1,
    },
    placementRules: {
      surface: 'sand',
      minDistFromWater: 2,
      minDistFromOther: 2,
    },
    description: 'A beautiful garden that attracts butterflies',
    onPlace: null,
  },
};

// ---------------------------------------------------------------------------
// Query helpers
// ---------------------------------------------------------------------------

/**
 * Get all recipes the player has unlocked (based on discovery count).
 * @returns {Array<object>}
 */
export function getAvailableRecipes() {
  const { discovered } = getDiscoveryCount();
  return Object.values(RECIPE_REGISTRY).filter(
    recipe => discovered >= recipe.discoveryRequirement
  );
}

/**
 * Get only recipes the player can currently afford.
 * @returns {Array<object>}
 */
export function getAffordableRecipes() {
  return getAvailableRecipes().filter(recipe => checkAfford(recipe.cost));
}

/**
 * Check if a specific recipe is affordable.
 * @param {string} recipeId — key in RECIPE_REGISTRY
 * @returns {boolean}
 */
export function canAffordRecipe(recipeId) {
  const recipe = RECIPE_REGISTRY[recipeId];
  if (!recipe) return false;
  return checkAfford(recipe.cost);
}

/**
 * Get a single recipe by id.
 * @param {string} recipeId — key in RECIPE_REGISTRY
 * @returns {object|null}
 */
export function getRecipe(recipeId) {
  return RECIPE_REGISTRY[recipeId] || null;
}

/**
 * Get all recipes (for the crafting menu to show locked ones too).
 * @returns {Array<object>}
 */
export function getAllRecipes() {
  return Object.values(RECIPE_REGISTRY);
}

/**
 * Register an onPlace callback for a recipe (used by mesh modules).
 * @param {string} recipeId — key in RECIPE_REGISTRY
 * @param {Function} callback
 */
export function registerOnPlace(recipeId, callback) {
  const recipe = RECIPE_REGISTRY[recipeId];
  if (recipe) recipe.onPlace = callback;
}
